import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const KIND_WORD = { slab_piece: "Slab piece", slab: "Slab", remnant: "Remnant", sink: "Sink" };

// Hold slabs / remnants / sinks from inventory against a quote; shows what is not in stock.
export const QuoteStockDialog = ({ quoteId, open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!quoteId) return;
    try {
      const { data: d } = await api.get(`/quotes/${quoteId}/stock`);
      setData(d);
    } catch {
      toast.error("Could not load stock for this quote");
    }
  }, [quoteId]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const hold = async (body) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/quotes/${quoteId}/stock/hold`, body);
      setData(d);
      onChanged?.(d);
      toast.success("Held for this quote");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not hold that");
    } finally {
      setBusy(false);
    }
  };
  const release = async (id) => {
    setBusy(true);
    try {
      const { data: d } = await api.delete(`/quotes/${quoteId}/stock/hold/${id}`);
      setData(d);
      onChanged?.(d);
      toast.success("Released back to stock");
    } catch {
      toast.error("Could not release that hold");
    } finally {
      setBusy(false);
    }
  };

  const btn = "rounded-md border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-indigo-500 hover:text-white disabled:opacity-40";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[88vh] w-[min(96vw,60rem)] max-w-none overflow-y-auto border-zinc-800 bg-zinc-950 text-zinc-100" data-testid="quote-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package size={18} weight="bold" className="text-indigo-300" /> Material & sinks from stock
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Hold a slab piece, remnant or sink against this quote. Anything still unheld when the quote is accepted raises an
            "Order needed" alert and puts the job on Waiting on Material.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <p className="py-8 text-center text-sm text-zinc-500">Checking inventory…</p>
        ) : (
          <div className="space-y-4">
            {data.order_alerts?.length > 0 && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="quote-stock-order-alerts">
                <span className="font-bold uppercase tracking-widest">Order needed</span>
                <ul className="mt-1 list-disc pl-4">{data.order_alerts.map((o) => <li key={o.id}>{o.detail}</li>)}</ul>
              </div>
            )}
            {data.areas.map((a) => {
              const matOk = a.material_held || !a.slab_color;
              return (
              <section key={a.area_id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-3" data-testid={`quote-stock-area-${a.area_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold">{a.name || "Area"}</span>
                  <span className="text-xs text-zinc-400">{a.slab_color || "no color picked"} · {a.sqft} sf · {a.sink_cutouts} sink cut-out{a.sink_cutouts === 1 ? "" : "s"}</span>
                  <span
                    data-testid={`quote-stock-status-${a.area_id}`}
                    className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      matOk && a.sinks_held >= a.sink_cutouts
                        ? "bg-emerald-500/15 text-emerald-200"
                        : a.options.pieces.length + a.options.remnants.length
                          ? "bg-amber-500/15 text-amber-200"
                          : "bg-red-500/15 text-red-200"
                    }`}
                  >
                    {matOk && a.sinks_held >= a.sink_cutouts
                      ? "Held from stock"
                      : a.options.pieces.length + a.options.remnants.length
                        ? "In stock — not held yet"
                        : "Not in stock"}
                  </span>
                </div>
                {a.holds.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {a.holds.map((h) => (
                      <li key={h.id} className="flex items-center gap-2 rounded-md bg-zinc-950 px-2 py-1 text-xs" data-testid={`quote-stock-hold-${h.id}`}>
                        <span className="rounded bg-indigo-500/20 px-1.5 text-[9px] font-bold uppercase tracking-widest text-indigo-200">{KIND_WORD[h.kind] || h.kind}</span>
                        <span className="flex-1 truncate">{h.label}</span>
                        <button type="button" disabled={busy} data-testid={`quote-stock-release-${h.id}`} onClick={() => release(h.id)} className="text-zinc-500 hover:text-red-300">
                          <X size={12} weight="bold" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {!a.material_held && (a.options.pieces.length > 0 || a.options.remnants.length > 0) && (
                  <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                    {a.options.pieces.map((p, i) => (
                      <button key={`${p.slab_id}-${p.piece_id || i}`} type="button" disabled={busy} data-testid={`quote-stock-pick-piece-${a.area_id}-${i}`}
                        onClick={() => hold({ area_id: a.area_id, kind: p.piece_id ? "slab_piece" : "slab", ref_id: p.slab_id, piece_id: p.piece_id || "" })}
                        className={`${btn} text-left normal-case tracking-normal`}>
                        <span className="block font-bold">{p.code} · {p.serial}</span>
                        <span className="block text-[10px] text-zinc-400">{p.length_in}" × {p.width_in}" {p.location ? `· ${p.location}` : ""}</span>
                      </button>
                    ))}
                    {a.options.remnants.map((r, i) => (
                      <button key={r.id} type="button" disabled={busy} data-testid={`quote-stock-pick-remnant-${a.area_id}-${i}`}
                        onClick={() => hold({ area_id: a.area_id, kind: "remnant", ref_id: r.id, sqft: a.sqft })}
                        className={`${btn} text-left normal-case tracking-normal`}>
                        <span className="block font-bold">Remnant {r.code}</span>
                        <span className="block text-[10px] text-zinc-400">{r.remaining_sqft} sf left · {r.length_in}" × {r.width_in}"</span>
                      </button>
                    ))}
                  </div>
                )}
                {a.sink_cutouts > a.sinks_held && data.sinks.length > 0 && (
                  <div className="mt-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Hold a sink ({a.sink_cutouts - a.sinks_held} still needed)</span>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {data.sinks.map((s, i) => (
                        <button key={s.id} type="button" disabled={busy} data-testid={`quote-stock-pick-sink-${a.area_id}-${i}`}
                          onClick={() => hold({ area_id: a.area_id, kind: "sink", ref_id: s.id, qty: 1 })} className={`${btn} normal-case tracking-normal`}>
                          {s.brand} {s.model} · {s.quantity} on hand
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {a.sink_cutouts > a.sinks_held && data.sinks.length === 0 && (
                  <p className="mt-2 text-xs text-red-300" data-testid={`quote-stock-no-sinks-${a.area_id}`}>No sinks in stock — will raise an order alert on acceptance.</p>
                )}
              </section>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
